/**
 * PATH: apps/quote/src/components/builder/panels/context-panel/FilesTab.tsx
 * PURPOSE: Files tab with upload, list, pin, delete, preview
 */

import { useState, useRef, useCallback } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Upload, Loader2, Paperclip, AlertCircle } from 'lucide-react'
import { cn } from '@/lib/utils'
import { quoteApi, type QuoteAttachment } from '@/lib/quoteApi'
import { useToast } from '../../Toast'
import FileItem from './FileItem'
import FilePreview from './FilePreview'

interface FilesTabProps {
  quoteId: string
  isEditable: boolean
  onLinkToRow?: (attachmentId: string) => void
}

export default function FilesTab({ quoteId, isEditable, onLinkToRow }: FilesTabProps) {
  const queryClient = useQueryClient()
  const toast = useToast()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [pinningId, setPinningId] = useState<string | null>(null)
  const [deletingId, setDeletingId] = useState<string | null>(null)
  const [previewingFile, setPreviewingFile] = useState<QuoteAttachment | null>(null)

  const { data: files = [], isLoading, error } = useQuery({
    queryKey: ['quoteAttachments', quoteId, 'file'],
    queryFn: () => quoteApi.listQuoteAttachments(quoteId, 'file'),
    staleTime: 30 * 1000,
  })

  const uploadMutation = useMutation({
    mutationFn: async (file: File) => quoteApi.uploadQuoteAttachment(quoteId, file, { attachmentType: 'file' }),
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['quoteAttachments', quoteId] }); toast.success('File uploaded successfully') },
    onError: (err: any) => { toast.error(err?.response?.data?.error || 'Failed to upload file') },
    onSettled: () => { setIsUploading(false) },
  })

  const togglePinMutation = useMutation({
    mutationFn: async (file: QuoteAttachment) => { setPinningId(file.id); return quoteApi.toggleQuoteAttachmentPin(quoteId, file.id, !file.is_pinned) },
    onSuccess: (_, file) => { queryClient.invalidateQueries({ queryKey: ['quoteAttachments', quoteId] }); toast.success(file.is_pinned ? 'Unpinned' : 'Pinned') },
    onError: () => { toast.error('Failed to update pin status') },
    onSettled: () => { setPinningId(null) },
  })

  const deleteMutation = useMutation({
    mutationFn: async (file: QuoteAttachment) => { setDeletingId(file.id); return quoteApi.deleteQuoteAttachment(quoteId, file.id) },
    onSuccess: () => { queryClient.invalidateQueries({ queryKey: ['quoteAttachments', quoteId] }); toast.success('File deleted') },
    onError: () => { toast.error('Failed to delete file') },
    onSettled: () => { setDeletingId(null) },
  })

  const handleFileSelect = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setIsUploading(true)
    uploadMutation.mutate(file)
    if (fileInputRef.current) fileInputRef.current.value = ''
  }, [uploadMutation])

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    const file = e.dataTransfer.files?.[0]
    if (!file) return
    setIsUploading(true)
    uploadMutation.mutate(file)
  }, [uploadMutation])

  const handleDragOver = useCallback((e: React.DragEvent) => { e.preventDefault() }, [])

  const handleOpenExternal = useCallback((file: QuoteAttachment) => { if (file.file_url) window.open(file.file_url, '_blank') }, [])

  const handleDownload = useCallback((file: QuoteAttachment) => {
    if (file.file_url) { const a = document.createElement('a'); a.href = file.file_url; a.download = file.original_filename; a.click() }
  }, [])

  if (previewingFile) {
    return (
      <FilePreview
        file={previewingFile}
        onBack={() => setPreviewingFile(null)}
        onOpenExternal={() => handleOpenExternal(previewingFile)}
        onDownload={() => handleDownload(previewingFile)}
        onLinkToRow={onLinkToRow ? () => onLinkToRow(previewingFile.id) : undefined}
      />
    )
  }

  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-slate-900 dark:text-white">Attachments</h3>
        {isEditable && (
          <>
            <input ref={fileInputRef} type="file" onChange={handleFileSelect} className="hidden" accept=".pdf,.doc,.docx,.xls,.xlsx,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv" />
            <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="flex items-center gap-1 px-2 py-1 text-xs bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed">
              {isUploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
              Upload
            </button>
          </>
        )}
      </div>

      {error && (
        <div className="flex items-center gap-2 p-2 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-lg text-sm">
          <AlertCircle className="w-4 h-4" /> Failed to load files
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center justify-center py-6"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>
      ) : files.length === 0 ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          className={cn('text-center py-6 border-2 border-dashed rounded-lg transition-colors', isEditable ? 'border-slate-200 dark:border-slate-700 hover:border-blue-400 dark:hover:border-blue-600 cursor-pointer' : 'border-slate-200 dark:border-slate-700')}
          onClick={() => isEditable && fileInputRef.current?.click()}
        >
          <Paperclip className="w-8 h-8 mx-auto text-slate-300 dark:text-slate-600 mb-1" />
          <p className="text-sm text-slate-500 dark:text-slate-400">No files attached</p>
          {isEditable && <p className="text-xs text-slate-400 dark:text-slate-500 mt-0.5">Drop files here or click to upload</p>}
        </div>
      ) : (
        <div className="space-y-2">
          {files.map((file) => (
            <FileItem
              key={file.id}
              file={file}
              quoteId={quoteId}
              isEditable={isEditable}
              onTogglePin={(f) => togglePinMutation.mutate(f)}
              onDelete={(f) => { if (window.confirm(`Delete "${f.original_filename}"?`)) deleteMutation.mutate(f) }}
              onPreview={setPreviewingFile}
              isPinning={pinningId === file.id}
              isDeleting={deletingId === file.id}
            />
          ))}
        </div>
      )}

      {isEditable && files.length > 0 && (
        <div className="pt-3 border-t border-slate-200 dark:border-slate-700">
          <button onClick={() => fileInputRef.current?.click()} disabled={isUploading} className="w-full flex items-center justify-center gap-2 px-3 py-2 border-2 border-dashed border-blue-300 dark:border-blue-700 text-blue-600 dark:text-blue-400 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 text-sm disabled:opacity-50">
            <Upload className="w-4 h-4" />
            {isUploading ? 'Uploading...' : 'Upload More Files'}
          </button>
        </div>
      )}
    </div>
  )
}
